import { apiFetch } from "./apiClient";
/**
 * AI Reply Service
 * Uses OpenRouter → Google Gemma 4 26B A4B (free) to generate high-converting email replies
 * based on company knowledge from the company profile.
 */

const BACKEND_URL = "https://email-syncing-backend.onrender.com";

/**
 * Fetch the company profile for the given user.
 * Returns the nested company object + knowledge base text.
 */
export async function fetchCompanyProfile(userId) {
  try {
    const res = await apiFetch(`${BACKEND_URL}/api/company-profile/${userId}`);
    const json = await res.json();
    if (json.success && json.data) {
      return json.data; // { company: {...}, services: [...], faqs: [...], companyKnowledge: "...", ... }
    }
    return null;
  } catch (err) {
    console.warn("Could not load company profile for AI reply:", err.message);
    return null;
  }
}

/**
 * Generate an AI email reply via the backend OpenRouter endpoint.
 *
 * @param {string} customerEmail - The customer's original email text
 * @param {object|null} profile - Company profile object (optional)
 * @param {string} customerName - Name of the customer (optional)
 * @returns {Promise<string>} - The generated email reply text
 */
export async function generateAiReply(customerEmail, profile, customerName = "") {
  const userId = localStorage.getItem("userid");

  const token = localStorage.getItem("usertoken");

  const response = await apiFetch(`${BACKEND_URL}/auth/generate-ai-reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      userId,
      customerEmail,
      customerName,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.message || `Backend AI reply error ${response.status}`);
  }

  const data = await response.json();
  if (!data.success || !data.reply) {
    throw new Error(data.message || "No reply generated by AI model.");
  }

  return data.reply;
}

/**
 * Decrement the AI replies counter for the user after a successful send.
 * This calls the backend to increment aiRepliesUsed by 1.
 *
 * @param {string} userId
 * @param {object} emailMeta - { customerName, businessEmail, scenarioName, service, inboundMessage, generatedReply }
 */
export async function recordAiReplyUsed(userId, emailMeta = {}) {
  try {
    await apiFetch(`${BACKEND_URL}/auth/increment-ai-replies/${userId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        customerName: emailMeta.customerName || "",
        businessEmail: emailMeta.businessEmail || "",
        scenarioName: emailMeta.scenarioName || "AI Reply",
        service: emailMeta.service || "General",
        inboundMessage: emailMeta.inboundMessage || "",
        generatedReply: emailMeta.generatedReply || "",
        scenarioType: emailMeta.scenarioType || "custom",
        status: "success",
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (err) {
    // Non-critical — log but don't block
    console.warn("Could not record AI reply usage:", err.message);
  }
}
